export type Unit = 'ppm' | '%' | 'g/t' | 'mg/L';

export interface MethodElementLimit {
  element: string;
  limit: number;
  unit: Unit;
  limitInPercent?: number;
}

export type MethodCode = 'D4A-ICP- PRE.MO/ANA/038' | 'FP-ICP PRE.MO/ANA/007' | 'BGM-MULTI-LAB' | string;

// ICP 4 acides limits (from provided table)
const ICP4A: Record<string, MethodElementLimit> = {
  // Majors as %
  Al: { element: 'Al', limit: 0.01, unit: '%' },
  Ca: { element: 'Ca', limit: 0.01, unit: '%' },
  Fe: { element: 'Fe', limit: 0.01, unit: '%' },
  K: { element: 'K', limit: 0.01, unit: '%' },
  Mg: { element: 'Mg', limit: 0.01, unit: '%' },
  Mn: { element: 'Mn', limit: 0.01, unit: '%' },
  Na: { element: 'Na', limit: 0.01, unit: '%' },
  P: { element: 'P', limit: 0.01, unit: '%' },
  S: { element: 'S', limit: 0.01, unit: '%' },
  Si: { element: 'Si', limit: 0.01, unit: '%' },
  Ti: { element: 'Ti', limit: 0.01, unit: '%' },
  // Traces ppm
  Ag: { element: 'Ag', limit: 1, unit: 'ppm' },
  As: { element: 'As', limit: 8, unit: 'ppm' },
  B: { element: 'B', limit: 5, unit: 'ppm' },
  Ba: { element: 'Ba', limit: 2, unit: 'ppm' },
  Be: { element: 'Be', limit: 1, unit: 'ppm' },
  Bi: { element: 'Bi', limit: 10, unit: 'ppm' },
  Cd: { element: 'Cd', limit: 1, unit: 'ppm' },
  Co: { element: 'Co', limit: 5, unit: 'ppm' },
  Cr: { element: 'Cr', limit: 5, unit: 'ppm' },
  Cu: { element: 'Cu', limit: 2, unit: 'ppm', limitInPercent: 0.01 },
  Ga: { element: 'Ga', limit: 5, unit: 'ppm' },
  Ge: { element: 'Ge', limit: 10, unit: 'ppm' },
  In: { element: 'In', limit: 5, unit: 'ppm' },
  Li: { element: 'Li', limit: 2, unit: 'ppm' },
  Mo: { element: 'Mo', limit: 1, unit: 'ppm' },
  Nb: { element: 'Nb', limit: 1, unit: 'ppm' },
  Ni: { element: 'Ni', limit: 2, unit: 'ppm' },
  Pb: { element: 'Pb', limit: 2, unit: 'ppm', limitInPercent: 0.01 },
  Sb: { element: 'Sb', limit: 1, unit: 'ppm' },
  Se: { element: 'Se', limit: 2, unit: 'ppm' },
  Sn: { element: 'Sn', limit: 2, unit: 'ppm' },
  Sr: { element: 'Sr', limit: 2, unit: 'ppm' },
  Ta: { element: 'Ta', limit: 1, unit: 'ppm' },
  Th: { element: 'Th', limit: 5, unit: 'ppm' },
  Tl: { element: 'Tl', limit: 1, unit: 'ppm' },
  V: { element: 'V', limit: 1, unit: 'ppm' },
  W: { element: 'W', limit: 2, unit: 'ppm' },
  Y: { element: 'Y', limit: 1, unit: 'ppm' },
  Zn: { element: 'Zn', limit: 2, unit: 'ppm', limitInPercent: 0.01 },
  Zr: { element: 'Zr', limit: 1, unit: 'ppm' },
};

// Rare earths (ppm)
const RARE_EARTHS: Record<string, MethodElementLimit> = {
  La: { element: 'La', limit: 1, unit: 'ppm' },
  Ce: { element: 'Ce', limit: 1, unit: 'ppm' },
  Pr: { element: 'Pr', limit: 5, unit: 'ppm' },
  Nd: { element: 'Nd', limit: 5, unit: 'ppm' },
  Sm: { element: 'Sm', limit: 5, unit: 'ppm' },
  Eu: { element: 'Eu', limit: 5, unit: 'ppm' },
  Gd: { element: 'Gd', limit: 5, unit: 'ppm' },
  Tb: { element: 'Tb', limit: 5, unit: 'ppm' },
  Dy: { element: 'Dy', limit: 5, unit: 'ppm' },
  Ho: { element: 'Ho', limit: 5, unit: 'ppm' },
  Er: { element: 'Er', limit: 5, unit: 'ppm' },
  Tm: { element: 'Tm', limit: 5, unit: 'ppm' },
  Yb: { element: 'Yb', limit: 5, unit: 'ppm' },
  Lu: { element: 'Lu', limit: 5, unit: 'ppm' },
  Sc: { element: 'Sc', limit: 5, unit: 'ppm' },
  U: { element: 'U', limit: 5, unit: 'ppm' },
};

// Fusion limits (subset from provided table)
const ICPFUSION: Record<string, MethodElementLimit> = {
  Al: { element: 'Al', limit: 0.01, unit: '%' },
  Ca: { element: 'Ca', limit: 0.01, unit: '%' },
  Fe: { element: 'Fe', limit: 0.01, unit: '%' },
  K: { element: 'K', limit: 0.01, unit: '%' },
  Mg: { element: 'Mg', limit: 0.01, unit: '%' },
  Na: { element: 'Na', limit: 0.01, unit: '%' },
  P: { element: 'P', limit: 0.01, unit: '%' },
  Si: { element: 'Si', limit: 0.01, unit: '%' },
  Ti: { element: 'Ti', limit: 0.01, unit: '%' },
  As: { element: 'As', limit: 8, unit: 'ppm' },
  Ba: { element: 'Ba', limit: 2, unit: 'ppm' },
  Be: { element: 'Be', limit: 1, unit: 'ppm' },
  Bi: { element: 'Bi', limit: 10, unit: 'ppm' },
  Cd: { element: 'Cd', limit: 1, unit: 'ppm' },
  Co: { element: 'Co', limit: 5, unit: 'ppm' },
  Cr: { element: 'Cr', limit: 5, unit: 'ppm' },
  Cu: { element: 'Cu', limit: 2, unit: 'ppm' },
  Li: { element: 'Li', limit: 20, unit: 'ppm' },
  Mo: { element: 'Mo', limit: 10, unit: 'ppm' },
  Ni: { element: 'Ni', limit: 20, unit: 'ppm' },
  Pb: { element: 'Pb', limit: 26, unit: 'ppm' },
  Sb: { element: 'Sb', limit: 1, unit: 'ppm' },
  Se: { element: 'Se', limit: 30, unit: 'ppm' },
  Sn: { element: 'Sn', limit: 20, unit: 'ppm' },
  Sr: { element: 'Sr', limit: 2, unit: 'ppm' },
  Ta: { element: 'Ta', limit: 1, unit: 'ppm' },
  Tl: { element: 'Tl', limit: 1, unit: 'ppm' },
  V: { element: 'V', limit: 1, unit: 'ppm' },
  Y: { element: 'Y', limit: 1, unit: 'ppm' },
  Zn: { element: 'Zn', limit: 2, unit: 'ppm' },
};



// ICP Lingots PRE.MO/ANA/082 (majors en element, %)
const LINGOTS_082: Record<string, MethodElementLimit> = {
  Al: { element: 'Al', limit: 0.01, unit: '%' },
  Ca: { element: 'Ca', limit: 0.01, unit: '%' },
  Fe: { element: 'Fe', limit: 0.01, unit: '%' },
  K: { element: 'K', limit: 0.01, unit: '%' },
  Mg: { element: 'Mg', limit: 0.01, unit: '%' },
  Mn: { element: 'Mn', limit: 0.01, unit: '%' },
  Na: { element: 'Na', limit: 0.01, unit: '%' },
  P: { element: 'P', limit: 0.01, unit: '%' },
  S: { element: 'S', limit: 0.01, unit: '%' },
  Ti: { element: 'Ti', limit: 0.01, unit: '%' },
  As: { element: 'As', limit: 8, unit: 'ppm' },
  Ba: { element: 'Ba', limit: 2, unit: 'ppm' },
  Be: { element: 'Be', limit: 1, unit: 'ppm' },
  Bi: { element: 'Bi', limit: 10, unit: 'ppm' },
  Cd: { element: 'Cd', limit: 1, unit: 'ppm' },
  Co: { element: 'Co', limit: 5, unit: 'ppm' },
  Cr: { element: 'Cr', limit: 5, unit: 'ppm' },
  Cu: { element: 'Cu', limit: 5, unit: 'ppm' },
  Ga: { element: 'Ga', limit: 5, unit: 'ppm' },
  Hg: { element: 'Hg', limit: 1, unit: 'ppm' },
  Li: { element: 'Li', limit: 2, unit: 'ppm' },
  Mo: { element: 'Mo', limit: 2, unit: 'ppm' },
  Ni: { element: 'Ni', limit: 2, unit: 'ppm' },
  Pb: { element: 'Pb', limit: 2, unit: 'ppm' },
  Sb: { element: 'Sb', limit: 1, unit: 'ppm' },
  Se: { element: 'Se', limit: 2, unit: 'ppm' },
  Sn: { element: 'Sn', limit: 2, unit: 'ppm' },
  Sr: { element: 'Sr', limit: 2, unit: 'ppm' },
  Tl: { element: 'Tl', limit: 1, unit: 'ppm' },
  V: { element: 'V', limit: 1, unit: 'ppm' },
  W: { element: 'W', limit: 2, unit: 'ppm' },
  Zn: { element: 'Zn', limit: 2, unit: 'ppm' },
  Zr: { element: 'Zr', limit: 1, unit: 'ppm' },
};

// ICP solutions usine PRE.MO/ANA/083 (mg/L)
const SOLUTIONS_083: Record<string, MethodElementLimit> = {
  Ag: { element: 'Ag', limit: 0.01, unit: 'mg/L' },
  Al: { element: 'Al', limit: 0.01, unit: 'mg/L' },
  As: { element: 'As', limit: 0.01, unit: 'mg/L' },
  Ba: { element: 'Ba', limit: 0.01, unit: 'mg/L' },
  Be: { element: 'Be', limit: 0.01, unit: 'mg/L' },
  Bi: { element: 'Bi', limit: 0.01, unit: 'mg/L' },
  Ca: { element: 'Ca', limit: 0.01, unit: 'mg/L' },
  Cd: { element: 'Cd', limit: 0.01, unit: 'mg/L' },
  Co: { element: 'Co', limit: 0.01, unit: 'mg/L' },
  Cr: { element: 'Cr', limit: 0.01, unit: 'mg/L' },
  Cu: { element: 'Cu', limit: 0.01, unit: 'mg/L' },
  Fe: { element: 'Fe', limit: 0.01, unit: 'mg/L' },
  Ga: { element: 'Ga', limit: 0.01, unit: 'mg/L' },
  Hg: { element: 'Hg', limit: 0.01, unit: 'mg/L' },
  K: { element: 'K', limit: 0.01, unit: 'mg/L' },
  Li: { element: 'Li', limit: 0.01, unit: 'mg/L' },
  Mg: { element: 'Mg', limit: 0.01, unit: 'mg/L' },
  Mn: { element: 'Mn', limit: 0.01, unit: 'mg/L' },
  Mo: { element: 'Mo', limit: 0.01, unit: 'mg/L' },
  Na: { element: 'Na', limit: 0.01, unit: 'mg/L' },
  Ni: { element: 'Ni', limit: 0.01, unit: 'mg/L' },
  P: { element: 'P', limit: 0.01, unit: 'mg/L' },
  Pb: { element: 'Pb', limit: 0.01, unit: 'mg/L' },
  S: { element: 'S', limit: 0.01, unit: 'mg/L' },
  Sb: { element: 'Sb', limit: 0.01, unit: 'mg/L' },
  Se: { element: 'Se', limit: 0.01, unit: 'mg/L' },
  Si: { element: 'Si', limit: 0.01, unit: 'mg/L' },
  Sn: { element: 'Sn', limit: 0.01, unit: 'mg/L' },
  Sr: { element: 'Sr', limit: 0.01, unit: 'mg/L' },
  Ti: { element: 'Ti', limit: 0.01, unit: 'mg/L' },
  Tl: { element: 'Tl', limit: 0.01, unit: 'mg/L' },
  V: { element: 'V', limit: 0.01, unit: 'mg/L' },
  W: { element: 'W', limit: 0.01, unit: 'mg/L' },
  Zn: { element: 'Zn', limit: 0.01, unit: 'mg/L' },
  Zr: { element: 'Zr', limit: 0.01, unit: 'mg/L' },
};

export const METHOD_LIMITS: Record<MethodCode, Record<string, MethodElementLimit>> = {
  'PRE.MO/ANA/038': { ...ICP4A, ...RARE_EARTHS },
  'PRE.MO/ANA/082': LINGOTS_082,
  'PRE.MO/ANA/083': SOLUTIONS_083,
  'D4A-ICP- PRE.MO/ANA/038': { ...ICP4A, ...RARE_EARTHS }, // 41 or 55 with rares
  'FP-ICP PRE.MO/ANA/007': ICPFUSION,
  'BGM-MULTI-LAB': {
    Ag: { element: 'Ag', limit: 1, unit: 'g/t' },
    As: { element: 'As', limit: 1, unit: 'ppm' },
    Mo: { element: 'Mo', limit: 1, unit: 'ppm' },
    Sn: { element: 'Sn', limit: 1, unit: 'ppm' },
    Cu: { element: 'Cu', limit: 0.01, unit: '%' },
    Fe: { element: 'Fe', limit: 0.01, unit: '%' },
    Pb: { element: 'Pb', limit: 0.01, unit: '%' },
    Zn: { element: 'Zn', limit: 0.01, unit: '%' },
    Au: { element: 'Au', limit: 0.05, unit: 'g/t' },
  },
};

export function getMethodDetectionLimit(methodCode: MethodCode | undefined, element: string): MethodElementLimit | undefined {
  if (!methodCode) return undefined;
  let table = METHOD_LIMITS[methodCode];
  if (!table) {
    // robust fallback: match by embedded PRE.MO/ANA/0XX code (analyses_demandees stores bare codes)
    const m = String(methodCode).match(/ANA\/(\d{3})/);
    if (m) table = METHOD_LIMITS['PRE.MO/ANA/' + m[1]];
  }
  if (!table) return undefined;
  return table[element] || table[element?.trim()] || table[element?.toUpperCase()];
}

export function formatWithMethodDetectionLimitFrontend(methodCode: MethodCode | undefined, element: string, value: number): string {
  const limit = getMethodDetectionLimit(methodCode, element);
  if (!limit) {
    return Number.isFinite(value) ? value.toString() : '0';
  }
  if (value <= 0) return `<${limit.limit}`;
  if (value < limit.limit) return `<${limit.limit}`;

  // Golden rule: ppm > 10000 => %
  let displayValue = value;
  let displayUnit: Unit = limit.unit;
  let displayLod = limit.limit;
  if (displayUnit === 'ppm' && value > 10000) {
    displayValue = value / 10000;
    displayUnit = '%';
    displayLod = limit.limitInPercent ?? 0.01;
    if (displayValue < displayLod) return `<${displayLod}`;
  }

  if (displayUnit === 'mg/L') return displayValue.toFixed(2);
  if (displayUnit === '%') return displayValue.toFixed(2);
  if (displayUnit === 'g/t') return displayValue.toFixed(2);
  return displayValue.toFixed(3);
}


